// Inline `@fun.name(...)` call expansion for declaration-value usage.
// Example: `font-size:@fun.font-size(2);` becomes `font-size:24px;`.
import { findMatchingPair } from '../../loops/for-loop/scanner-utils'
import type { LuisFunctionCall } from '../types'
import { expandFunctionCall } from './expand-call'
import type { ExpandNestedCalls, ExpansionContext } from './expand-types'
import { parseFunctionCallStatement } from './parse'
import { pushExpansionWarning } from './warnings'

// Expand inline `@fun.` calls that appear inside otherwise normal source lines.
/** @internal Expand inline `@fun.` calls inside internal function-expansion helpers. */
export function expandInlineFunctionCalls(
  line: string,
  lineNumber: number,
  context: ExpansionContext,
  stack: string[],
  depth: number,
  expandNestedCalls: ExpandNestedCalls,
): string {
  let output = ''
  let quote: "'" | '"' | null = null
  let changed = false
  // Scan line content and replace inline `@fun.` calls outside quotes.
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index]
    const previous = line[index - 1] ?? ''
    if (quote) {
      output += char
      if (char === quote && previous !== '\\') quote = null
      continue
    }
    if (char === "'" || char === '"') {
      quote = char
      output += char
      continue
    }
    const callMatch = parseInlineCallAt(line, index)
    if (!callMatch) {
      output += char
      continue
    }
    const definition = context._functions[callMatch.call.name]
    if (!definition) {
      pushExpansionWarning(context, 'unknown-function-call', lineNumber, `Function "${callMatch.call.name}" is not defined.`)
      output += callMatch.raw
      index = callMatch.end - 1
      continue
    }
    output = trimPropertyValueGap(output)
    output += normalizeInlineExpansion(
      expandFunctionCall(definition, callMatch.call, context, stack, depth, lineNumber, expandNestedCalls),
    )
    index = callMatch.end - 1
    changed = true
  }
  // Return the original line when no inline call changed.
  return changed ? output : line
}

// Parse one inline `@fun.name(...)` call that starts at the provided index.
function parseInlineCallAt(source: string, startIndex: number): { call: LuisFunctionCall; raw: string; end: number } | null {
  if (source.slice(startIndex, startIndex + 5) !== '@fun.') return null
  const openIndex = source.indexOf('(', startIndex + 5)
  if (openIndex === -1) return null
  const closeIndex = findMatchingPair(source, openIndex, '(', ')')
  if (closeIndex === -1) return null
  const raw = source.slice(startIndex, closeIndex + 1)
  const call = parseFunctionCallStatement(raw)
  return call ? { call, raw, end: closeIndex + 1 } : null
}

// Normalize expanded inline function bodies so they fit declaration values.
function normalizeInlineExpansion(source: string): string {
  return source.replace(/;\s*/g, '').replace(/\s+/g, ' ').trim()
}

// Trim source spacing so `prop: @fun.x()` keeps one normalized value gap.
function trimPropertyValueGap(source: string): string {
  return /:\s+$/.test(source) ? source.replace(/\s+$/, '') : source
}
